import React from 'react';
import { useParams } from 'react-router';
import { defaultTo, get } from 'lodash';
import intersectionBy from 'lodash/intersectionBy';
import { PartnerProductCatalogueItem } from '@atlassian/cssxp-types-product';
import { ProxyGraphClients, abgGraphqlClient as graphqlClient } from '@atlassian/temp-client-utils';
import {
  ColGroup,
  CustomDynamicResultTable,
  TableBody,
  TableHead,
  errorNotificationBuilder,
  useNotification,
} from '@css-xp/base';
import {
  ATLAS_PRODUCT_ID,
  CATALOG_EXCLUDE_PRODUCTS,
  JIRA_PRODUCT_IDS,
  LOOM_PRODUCT_ID,
  ROVO_PRODUCT_ID,
  STRATEGY_COLLECTION_PRODUCT_ID,
  TEAMWORK_COLLECTION_PRODUCT_ID,
} from '@css-xp/ppc-constants';
import { fetchNativeProductCatalogueWithOfferingAndPricingPlans } from '@css-xp/ppc-api';
import {
  constructQuerySearchParams,
  usePartnerCatalogueContext,
  usePartnerProductCatalogueContext,
} from '@css-xp/ppc-checkout';
import {
  emptyStateProp,
  errorStateProp,
  emptyStatePropForAddAppToProductFlow,
  emptyStatePropForAddOnsToProductFlow,
  emptyStatePropForAddAppOrAddOnsToProductFlow,
  emptyStatePropForAddMarketplaceAppOrAddOns,
} from '@css-xp/ppc-common';
import { createHead, createRows } from './products-table-items';
import { getPricingPlanFilterOptionsForNativeCatalog } from '../../../../store/utils/get-catalog-pricing-plan-filters';
import './products-table.scss';

export const ProductsTableOrderFlow = ({
  allowedParentProductIdsForAppsOrder,
  availableProductsApps,
  appsDataLoading,
  isJWMDisabled,
  isAtlasDisabled,
  isLoomDisabled,
  isSandboxCloudsite,
  isMultiCurrencyEnabled = false,
  showCheckbox = false,
  checkedProductsList,
  setCheckedProductsList,
  parentContainerOrProductId = null,
  quoteItemsInCart,
  isDesignIteration1Enabled,
  isAddOnsSearchEnabled,
  isAppificationEnabled,
  isAppsSearchEnabled,
}: {
  allowedParentProductIdsForAppsOrder?: string[];
  isJWMDisabled?: boolean;
  isAtlasDisabled?: boolean;
  isLoomDisabled?: boolean;
  isSandboxCloudsite?: boolean;
  isMultiCurrencyEnabled?: boolean;
  showCheckbox?: boolean;
  checkedProductsList?: PartnerProductCatalogueItem[];
  availableProductsApps?: any;
  appsDataLoading?: boolean;
  setCheckedProductsList?: (_checkedProductsList: PartnerProductCatalogueItem[]) => void;
  parentContainerOrProductId?: string | null;
  quoteItemsInCart?: PartnerProductCatalogueItem[];
  isDesignIteration1Enabled?: boolean;
  isAddOnsSearchEnabled?: boolean;
  isAppificationEnabled?: boolean;
  isAppsSearchEnabled?: boolean;
}) => {
  const [tableRows, setTableRows] = React.useState<PartnerProductCatalogueItem[]>([]);
  const [tableLevelLoading, setTableLevelLoading] = React.useState<boolean>(false);
  const { txaId } = useParams<{ txaId: string }>();

  React.useEffect(() => {
    if (availableProductsApps?.nodes) {
      setTableRows(availableProductsApps?.nodes);
    }
  }, [availableProductsApps?.nodes]);

  React.useEffect(() => {
    if (appsDataLoading) {
      setTableLevelLoading(true);
    } else {
      setTableLevelLoading(false);
    }
  }, [appsDataLoading]);

  const catalogExcludedProducts: string[] = [];

  if (isAtlasDisabled) {
    catalogExcludedProducts.push(ATLAS_PRODUCT_ID);
  }

  if (isJWMDisabled) {
    // JWM product id
    catalogExcludedProducts.push(JIRA_PRODUCT_IDS.JWM);
  }

  if (isLoomDisabled) {
    // add Loom product id in excluding list
    catalogExcludedProducts.push(LOOM_PRODUCT_ID);
  }

  // add Teamwork collection product id in excluding list
  catalogExcludedProducts.push(TEAMWORK_COLLECTION_PRODUCT_ID);

  // add Strategy collection product id in excluding list
  catalogExcludedProducts.push(STRATEGY_COLLECTION_PRODUCT_ID);

  // Not showing Rovo product in UOF, adding Rovo product id in exclude list
  catalogExcludedProducts.push(ROVO_PRODUCT_ID);

  const {
    filterState,
    data: catalogueData,
    isTableLoading,
    searchResults,
    querySearch,
    setQuerySearch,
    existingCloudSiteProductIds,
  } = usePartnerProductCatalogueContext();

  const {
    quoteItems,
    setCatalogueItem,
    setQuoteItems,
    setIsPartnerCatalogueInformationLoading,
    setEstimatesToBeRefreshed,
  } = usePartnerCatalogueContext();

  const { notificationSent, sendErrorNotification, setNotificationSent } = useNotification();

  const filteredProducts = filterState?.products;
  const hasFilteredProducts = filteredProducts?.length > 0;
  // Note: Initially `searchResults` is undefined and changes to an array after searching
  const hasFilterOrSearchApplied = hasFilteredProducts || searchResults;
  const hasFilterAndSearchApplied = hasFilteredProducts && searchResults;
  let products: any = [];

  if (hasFilterAndSearchApplied) {
    products = intersectionBy(filteredProducts, searchResults, 'name');
  } else if (searchResults) {
    products = searchResults;
  } else if (hasFilteredProducts) {
    products = filteredProducts;
  }

  const createTableHead = React.useCallback(() => {
    return createHead(showCheckbox, isAppificationEnabled);
  }, [showCheckbox, isAppificationEnabled]);

  const createTableRows = React.useCallback(() => {
    return createRows({
      products: tableRows,
      quoteItems: quoteItemsInCart ?? quoteItems,
      setCatalogueItem,
      setIsPartnerCatalogueInformationLoading,
      txaId,
      setEstimatesToBeRefreshed,
      isRowClickable: true,
      existingCloudSiteProductIds,
      allowedParentProductIdsForAppsOrder,
      catalogExcludedProducts,
      isSandboxCloudsite,
      showCheckbox,
      checkedProductsList,
      setCheckedProductsList,
      parentContainerOrProductId,
      isDesignIteration1Enabled,
    });
  }, [
    setIsPartnerCatalogueInformationLoading,
    setCatalogueItem,
    quoteItems,
    tableRows,
    txaId,
    setEstimatesToBeRefreshed,
    existingCloudSiteProductIds,
    allowedParentProductIdsForAppsOrder,
    catalogExcludedProducts,
    isSandboxCloudsite,
    showCheckbox,
    checkedProductsList,
    setCheckedProductsList,
    parentContainerOrProductId,
  ]);

  React.useEffect(() => {
    //storing product data of default result
    catalogueData && setTableRows([...catalogueData?.nativeCatalog?.nodes]);
    const cursor = defaultTo(get(catalogueData, 'nativeCatalog.pageInfo.endCursor'), undefined);
    const hasNextPage = defaultTo(get(catalogueData, 'nativeCatalog.pageInfo.hasNextPage'), false);
    // Storing all the page params into product catalogue context since it is used in other page as well
    setQuerySearch(constructQuerySearchParams(cursor, hasNextPage, querySearch?.pageParams?.searchText));
  }, [catalogueData]);
  React.useEffect(() => {
    //storing product data of search result
    if (hasFilterOrSearchApplied) {
      setTableRows([...products]);
    }
  }, [hasFilterOrSearchApplied]);

  const onLoadMore = React.useCallback(async () => {
    const endCursor = defaultTo(get(querySearch, 'variables.pageParams.cursor'), undefined);

    if (endCursor) {
      let response;
      setTableLevelLoading(true);
      setQuerySearch({ ...querySearch, hasNextPage: false });
      try {
        response = await graphqlClient(ProxyGraphClients.SXP).query({
          query: fetchNativeProductCatalogueWithOfferingAndPricingPlans,
          variables: {
            ...querySearch.variables,
            pricingPlanFilterOptions: getPricingPlanFilterOptionsForNativeCatalog(isMultiCurrencyEnabled),
            filterOptions: [
              {
                key: 'includeProperties',
                values: ['vendorName'],
              },
            ],
          },
        });
      } catch (err) {
        // Remove the loading state for the products table
        setTableLevelLoading(false);
        if (!notificationSent) {
          const notificationOptions = errorNotificationBuilder()
            .withId('product-load-more-failure')
            .withMessage(defaultTo(get(err, 'message'), ''))
            .withDismissAfter(5000)
            .build();
          sendErrorNotification(notificationOptions);
          setNotificationSent(true);
        }
        return;
      }
      const node = defaultTo(get(response, 'data.nativeCatalog.nodes'), []).filter(
        (product: any) => product && !CATALOG_EXCLUDE_PRODUCTS.includes(product?.id)
      );
      const cursor = defaultTo(get(response, 'data.nativeCatalog.pageInfo.endCursor'), undefined);
      const hasNextPage = defaultTo(get(response, 'data.nativeCatalog.pageInfo.hasNextPage'), false);
      const searchText = defaultTo(get(querySearch, 'variables.searchText'), '');
      setTableRows([...tableRows, ...node]);
      setQuerySearch(constructQuerySearchParams(cursor, hasNextPage, searchText));
      setTableLevelLoading(false);
    }
  }, [tableRows, querySearch, setQuerySearch]);

  const getEmptyStateOrProps = () => {
    if (isDesignIteration1Enabled) {
      if (isAddOnsSearchEnabled && isAppsSearchEnabled) {
        if (isAppificationEnabled) {
          return emptyStatePropForAddMarketplaceAppOrAddOns;
        }
        return emptyStatePropForAddAppOrAddOnsToProductFlow;
      }
      if (isAddOnsSearchEnabled) {
        return emptyStatePropForAddOnsToProductFlow;
      }
      return emptyStatePropForAddAppToProductFlow;
    }
    return emptyStateProp;
  };

  return (
    <div className="products-table">
      <CustomDynamicResultTable
        className="products-list-table"
        loadingSpinnerSize="large"
        isLoading={isTableLoading || tableLevelLoading}
        testId="product-catalog-list"
        error=""
        isPaginationEnabled={false}
        showLoadMore={querySearch?.hasNextPage && !isTableLoading}
        loadMoreText={'Load more'}
        onLoadMore={onLoadMore}
        loadMoreLoading={!isDesignIteration1Enabled && tableLevelLoading}
        noResults={tableRows.length === 0}
        boxShadow={false}
        emptyStateProps={getEmptyStateOrProps()}
        errorStateProps={errorStateProp}
        {...(isDesignIteration1Enabled
          ? {
              loaderOptions: {
                hideSearchBar: true,
                hideFilters: true,
                hideActions: true,
                columnsCount: 2,
              },
            }
          : {})}
      >
        <ColGroup columns={createTableHead()} />
        <TableHead columns={createTableHead()} />
        <TableBody>{createTableRows()}</TableBody>
      </CustomDynamicResultTable>
    </div>
  );
};
